import React, { FC, ReactElement, useState } from "react";

import { TypeFiesta, TypeInsignia } from "@at/design-system-foundation";
import { IconInformation } from "@at/design-system-icons";
import {
  BulletList,
  BulletListType,
  Modal,
  ModalVariant,
} from "@at/design-system-patterns";
import {
  dataAttrGUI,
  dataAttrTestId,
} from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";

import * as Styled from "./description-list.styled";
import {
  IDescriptionListData,
  IDescriptionListProps,
} from "./description-list.types";
import { useTracking } from "../../../utils/tracking/use-tracking";

const shouldTruncateDetails = (term: string | number | ReactElement) => {
  if (term === "Dimensions" || term === "End layout") {
    return true;
  }
  return false;
};

const splitDataIntoColumnsByMaxItems = (
  data: IDescriptionListData[],
  maxColumnItems: number,
) => {
  const arrayOfColumns = [];
  let index = 0;
  while (index < data.length) {
    const chunk = data.slice(index, index + maxColumnItems);
    arrayOfColumns.push(chunk);
    index += maxColumnItems;
  }
  return arrayOfColumns;
};

const DescriptionList: FC<IDescriptionListProps> = ({
  id,
  data,
  maxItemsPerColumn,
  columnCount = 2,
  justifyGridContent = "space-between",
  minWidthColumnSplit = "48rem",
  maxColumnWidth = "1fr",
  compact = false,
  ...rest
}) => {
  const uniqueId = id ?? "description-list";

  const maxColumnItemsByColumnCount = Math.ceil(data.length / columnCount);
  const dataIntoColumns = splitDataIntoColumnsByMaxItems(
    data,
    maxItemsPerColumn ?? maxColumnItemsByColumnCount,
  );

  const calculatedColumnCountForCSSGrid =
    columnCount ?? dataIntoColumns.map((column) => column).length;

  const [visible, setVisible] = useState(false);
  const { trackModalEvent } = useTracking();

  return (
    <>
      <Styled.DescriptionListWrapper
        {...{ [dataAttrGUI]: uniqueId }}
        columnCount={calculatedColumnCountForCSSGrid}
        minWidthColumnSplit={minWidthColumnSplit}
        maxColumnWidth={maxColumnWidth}
        justifyGridContent={justifyGridContent}
        {...rest}
      >
        {dataIntoColumns.map((column, columnIndex) => (
          <Styled.Column
            {...{
              [dataAttrGUI]: `${uniqueId}-column`,
              [dataAttrTestId]: `${uniqueId}-column`,
            }}
            key={`${uniqueId}-column-${columnIndex}`}
          >
            {column.map(({ term, details }, rowIndex) => (
              <Styled.Row key={`${uniqueId}-row-${rowIndex}`}>
                <Styled.Term compact={compact} className="term_details">
                  {term}
                </Styled.Term>
                <Styled.Details
                  shouldTruncate={shouldTruncateDetails(term)}
                  compact={compact}
                  {...{ [dataAttrTestId]: "details" }}
                  className="value_details"
                >
                  {details}
                  {term === "Range" && (
                    <Styled.InfoButtonStyled
                      onClick={() => setVisible(true)}
                      aria-label="Info about the range"
                    >
                      <IconInformation
                        title="More information about the vehicles range"
                        customSize={{ height: 18, width: 18 }}
                      />
                    </Styled.InfoButtonStyled>
                  )}
                </Styled.Details>
              </Styled.Row>
            ))}
          </Styled.Column>
        ))}
      </Styled.DescriptionListWrapper>

      <Modal
        isShowing={visible}
        hasBackLink={false}
        closeText="Back"
        variation={ModalVariant.SLIDE}
        hideFn={() => setVisible(false)}
        portal={true}
        overlayName="range"
        trackingCallback={({ modalAction }) => {
          trackModalEvent({ trackingLabel: "range", modalAction });
        }}
      >
        <Styled.ModalBody>
          <TypeInsignia as="h2" weight="400">
            Electric vehicle range
          </TypeInsignia>
          <TypeFiesta as="p">
            The range of an electric vehicle is the number of miles it can
            travel with a fully charged battery before needing to be recharged.
            This information is provided by the manufacturer.
          </TypeFiesta>
          <TypeFiesta as="p">
            Whilst this information is useful, it’s important to remember that
            there are several factors that can affect a vehicle’s range.
          </TypeFiesta>
          <TypeFiesta as="p" weight="600">
            Some of the factors that can affect the range of an electric
            vehicle:
          </TypeFiesta>
          <BulletList
            listType={BulletListType.STANDARD}
            items={[
              `Weather conditions, especially cold weather`,
              `The vehicle load`,
              `Usage of heating and air-conditioning`,
              `Battery degradation over time`,
            ]}
          />
        </Styled.ModalBody>
      </Modal>
    </>
  );
};
export default DescriptionList;
